/**
 * Auth Token Service
 *
 * Provides a proper module-level interface for getting auth tokens,
 * replacing the previous `window.__getAuthToken` global pattern.
 *
 * AuthContext registers its getIdToken function via `setAuthTokenProvider()`.
 * All services/stores call `getAuthToken()` from this module instead of
 * reaching into `window.__getAuthToken`.
 */

type AuthTokenProvider = (forceRefresh?: boolean) => Promise<string>;

let _provider: AuthTokenProvider | null = null;

/**
 * Register the auth token provider function.
 * Called by AuthContext on mount.
 */
export function setAuthTokenProvider(provider: AuthTokenProvider): void {
  _provider = provider;
}

/**
 * Clear the auth token provider.
 * Called by AuthContext on unmount.
 */
export function clearAuthTokenProvider(): void {
  _provider = null;
}

/**
 * Get an authentication token.
 *
 * @param forceRefresh - If true, bypasses Firebase token cache (e.g. on 401)
 * @throws Error if no provider is registered (auth not initialized)
 */
export async function getAuthToken(forceRefresh?: boolean): Promise<string> {
  if (!_provider) {
    throw new Error('Authentication token not available');
  }
  return _provider(forceRefresh);
}

/**
 * Get auth token with retry logic.
 * Handles race conditions where auth is still initializing.
 *
 * @param maxRetries - Number of retries (default: 5)
 * @param retryDelay - Delay between retries in ms (default: 100)
 */
export async function getAuthTokenWithRetry(
  maxRetries: number = 5,
  retryDelay: number = 100
): Promise<string> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (_provider) {
      try {
        return await _provider();
      } catch (err) {
        if (
          attempt < maxRetries - 1 &&
          err instanceof Error &&
          err.message.includes('token not available')
        ) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        throw err;
      }
    }
    // Wait before retrying if provider isn't set yet
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }
  throw new Error('Authentication token not available');
}

/**
 * Check if an auth token provider is currently registered.
 * Useful for code that needs to wait for auth initialization.
 */
export function isAuthReady(): boolean {
  return _provider !== null;
}
